import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/20/solid'
import { services } from '../data/services'
import { renderHighlighted } from '../utils/renderHighlighted'

export default function ServiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeId, setActiveId] = useState(id ?? services[0].id)

  useEffect(() => {
    if (id) setActiveId(id)
  }, [id])

  const active = services.find((s) => s.id === activeId) ?? services[0]

  const handleTab = (serviceId: string) => {
    setActiveId(serviceId)
    navigate(`/services/${serviceId}`, { replace: true })
  }

  return (
    <div className="bg-white">
      {/* Back link */}
      <div className="mx-auto max-w-6xl px-6 pt-6 lg:px-8">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Home
        </button>
      </div>

      {/* Hero: title left, image right */}
      <div className="mx-auto max-w-6xl px-6 py-8 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2">
          <div>
            <h1 className="text-3xl font-bold leading-tight text-gray-900 sm:text-4xl">
              Services That Drive
            </h1>
            <h1 className="text-3xl font-bold leading-tight text-indigo-500 sm:text-4xl">
              Payments
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-gray-500">
              Expert consulting and certification services for the global payments ecosystem.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl shadow-lg">
            <img
              src="/meeting.png"
              alt="Team meeting"
              className="h-48 w-full object-cover lg:h-56"
            />
          </div>
        </div>
      </div>

      {/* Tab strip */}
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <div className="flex flex-wrap gap-3">
          {services.map((s) => (
            <button
              key={s.id}
              onClick={() => handleTab(s.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                activeId === s.id
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'border border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* Content card */}
      <div className="mx-auto max-w-6xl px-6 py-8 pb-24 lg:px-8">
        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900">{active.name}</h2>

          <div className="mt-5 space-y-4">
            {active.description.map((para, i) => (
              <p key={i} className="text-base leading-relaxed text-gray-600">
                {renderHighlighted(para)}
              </p>
            ))}
          </div>

          <div className="mt-6">
            <h3 className="text-base font-semibold text-gray-800">What We Offer</h3>
            <ul className="mt-3 space-y-2">
              {active.shortDesc.split(' and ').map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
